import classNames from 'classnames';
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CAIRNS_LOGO_IMAGE_URL } from 'assets/images/images';
import BackWithTitle from 'components/BackWithTitle/BackWithTitle';
import { Button } from 'components/Button/Button';
import { CheckCircleIcon, CrossCircleIcon } from 'components/Icons';
import { PaneBottomContactPanel } from 'components/PaneBottomContactPanel/PaneBottomContactPanel';
import { Text } from 'components/Typography';
import { AccountInput } from 'containers/Account/components/AccountInput/AccountInput';
import { Color } from 'utils/color';
import { RouteNames } from 'utils/navigationUtils';
import { CONFIRM_PASSWORD_CRITERIA, PASSWORD_CRITERIA } from 'utils/util';
import { SUPPORT_EMAIL } from '../../../../../../configConsts';
import style from './CreatePasswordPane.scss';

interface Props {
  acceptInvite: Function;
}

export const CreatePasswordPane: React.FC<Props> = ({ acceptInvite }) => {
  const passwordState: [string, Dispatch<SetStateAction<string>>] = useState<string>('');
  const confirmPasswordState: [string, Dispatch<SetStateAction<string>>] = useState<string>('');
  const maskPasswordState: [boolean, Dispatch<SetStateAction<boolean>>] = useState<boolean>(true);
  const maskConfirmPasswordState: [boolean, Dispatch<SetStateAction<boolean>>] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const email: string = location.state?.email;
  const token: string = location.state?.token;

  const [errors, setErrors] = useState({
    criteria: '',
    match: ''
  });
  const [emailSignupError, setEmailSignupError] = useState<string>('');

  const criteriaState = useMemo(
    () =>
      PASSWORD_CRITERIA?.map((c) => ({
        id: c.id,
        label: c.label,
        valid: c.test(passwordState[0])
      })),
    [passwordState[0]]
  );

  const confirmCriteriaState = useMemo(
    () =>
      CONFIRM_PASSWORD_CRITERIA?.map((c) => ({
        id: c.id,
        label: c.label,
        valid: c.test(passwordState[0], confirmPasswordState[0])
      })),
    [passwordState[0], confirmPasswordState[0]]
  );

  const getCriteriaColor = useCallback(
    (isValid: boolean) =>
      isValid ? { icon: Color.LIGHT_GREEN_200, text: Color.DARK_GREEN } : errors.criteria ? { icon: Color.RED_600, text: Color.RED_900 } : { icon: Color.GRAY, text: Color.GRAY },
    [errors]
  );

  const renderCriteriaList = useCallback(
    (list: { id: string; label: string; valid: boolean }[], relevantError: string) => {
      return list?.map((c) => (
        <li key={c.id}>
          {relevantError && !c.valid ? <CrossCircleIcon color={getCriteriaColor(c.valid).icon} /> : <CheckCircleIcon color={getCriteriaColor(c.valid).icon} />}
          <Text variant="md" color={getCriteriaColor(c.valid).text}>
            {c.label}
          </Text>
        </li>
      ));
    },
    [getCriteriaColor]
  );

  const checkPasswordErrors = useCallback((passValue, confirmPassValue): boolean => {
    let hasErrors = false;
    const newErrors = {
      criteria: '',
      match: ''
    };

    // Check password criteria
    const allCriteriaMet = PASSWORD_CRITERIA?.every((c) => c.test(passValue));
    if (!allCriteriaMet) {
      newErrors.criteria = 'Password does not meet all criteria';
      hasErrors = true;
    } else {
      newErrors.criteria = '';
    }

    // Check password match
    if (passValue !== confirmPassValue) {
      newErrors.match = 'Passwords do not match';
      hasErrors = true;
    } else {
      newErrors.match = '';
    }

    setErrors(newErrors);
    return !hasErrors;
  }, []);

  const onContinueClick = useCallback(async () => {
    setLoading(true);
    const isFormValid = checkPasswordErrors(passwordState[0], confirmPasswordState[0]);
    if (!isFormValid) {
      setLoading(false);
      return;
    }

    try {
      if (emailSignupError) {
        setEmailSignupError('');
      }
      await acceptInvite(
        {
          email,
          password: passwordState[0],
          isNewSignupFlow: true
        },
        token
      );

      navigate(RouteNames.SUCCESS_AUTH);
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setEmailSignupError(error?.data?.message || 'Email sign up failed. Please try again or use other method.');
      return;
    } finally {
      setLoading(false);
    }
  }, [checkPasswordErrors, passwordState[0], confirmPasswordState[0], navigate]);

  return (
    <div className={style.paneContainer}>
      <div className={style.paneContentWrapper}>
        <div className={style.mobileLogoWrapper}>
          <img src={CAIRNS_LOGO_IMAGE_URL} alt="Cairns Health" />
        </div>
        <div className={style.desktopBackButtonWrapper}>
          <BackWithTitle title="Back to sign up" textVariant="nlg" textColor={Color.DARK_BLUE_700} onClick={() => history.back()} />
        </div>
        <div className={style.textWrapper}>
          <Text variant="h5">Set your password for</Text>
          <Text variant="nlg" weight={600}>
            {email}
          </Text>
        </div>
        <div className={style.formWrapper}>
          {/* Password Input */}
          <div className={style.passwordInputContainer}>
            <div className={`${style.passwordInputWrapper} ${errors.criteria ? style.passwordInputContainerError : ''}`}>
              <Text variant="sm" weight={500} color={Color.GRAY_500} className={`${style.passwordInputLabel} ${errors.criteria ? style.passwordInputLabelError : ''}`}>
                Enter your password
              </Text>
              <div className={style.passwordInputField}>
                <AccountInput
                  inputState={passwordState}
                  maskInputState={maskPasswordState}
                  includeValidation={false}
                  onChange={(newValue) => {
                    errors.criteria && checkPasswordErrors(newValue, confirmPasswordState[0]);
                  }}
                />
              </div>
            </div>
          </div>

          <ul className={style.passwordCriteriaContainer} aria-live="polite">
            {renderCriteriaList(criteriaState, errors.criteria)}
          </ul>

          {/* Confirm Password Input */}
          <div className={style.passwordInputContainer}>
            <div className={`${style.passwordInputWrapper} ${errors.match ? style.passwordInputContainerError : ''}`}>
              <Text variant="sm" weight={500} color={Color.GRAY_500} className={`${style.passwordInputLabel} ${errors.match ? style.passwordInputLabelError : ''}`}>
                Repeat password
              </Text>
              <div className={style.passwordInputField}>
                <AccountInput
                  inputState={confirmPasswordState}
                  maskInputState={maskConfirmPasswordState}
                  includeValidation={false}
                  onChange={(newValue) => {
                    (errors.criteria || errors.match) && checkPasswordErrors(passwordState[0], newValue);
                  }}
                />
              </div>
            </div>
            <ul className={style.confirmPasswordCriteriaContainer} aria-live="polite">
              {renderCriteriaList(confirmCriteriaState, errors.match)}
            </ul>
          </div>
        </div>
        {!!emailSignupError && (
          <div className={style.authError}>
            <Text variant="sm" color={Color.RED_900}>
              {emailSignupError}
            </Text>
          </div>
        )}
        <Button className={classNames(style.completeButton, style.newAuthButton)} variant="contained" disabled={!passwordState[0].length || loading} onClick={onContinueClick}>
          Complete
        </Button>
      </div>
      <PaneBottomContactPanel email={SUPPORT_EMAIL} isMobileHidden />
    </div>
  );
};
